import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SpinnerGap, DownloadSimple, Eye, Funnel } from '@phosphor-icons/react';
import { FilterPanel, FilterField } from '../../components/FilterPanel';
import { ExportPreviewModal } from '../../components/ExportPreviewModal';
import { fmtINR, fmtUSD } from '../../utils/currency';
import { matchesPayrollStatus, isApprovedStatus, isRejectedStatus } from '../../utils/placementStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SwitchToggle = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={testId}>
    <div className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-slate-300'}`} onClick={() => onChange(!checked)}>
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </div>
    <span className="text-xs text-slate-700 font-medium">{label}</span>
  </label>
);

const PayrollExportPage = () => {
  const [placements, setPlacements] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [fMonth, setFMonth] = useState(String(new Date().getMonth() + 1));
  const [fYear, setFYear] = useState(String(new Date().getFullYear()));
  const [fOrg, setFOrg] = useState('all');
  const [fStatus, setFStatus] = useState('approved');
  const [fRecruiter, setFRecruiter] = useState('all');
  const [fTeam, setFTeam] = useState('all');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fSearch, setFSearch] = useState('');

  // Preview category toggles
  const [showApproved, setShowApproved] = useState(true);
  const [showRejected, setShowRejected] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [showMemberIncentives, setShowMemberIncentives] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pRes, uRes] = await Promise.all([
        axios.get(`${API_URL}/api/placements?page=1&limit=500`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const allPlacements = pRes.data?.placements || pRes.data || [];
      // If there are more pages, fetch them
      const total = pRes.data?.total || allPlacements.length;
      if (total > 500) {
        const pages = Math.ceil(total / 500);
        const moreCalls = [];
        for (let pg = 2; pg <= pages; pg++) {
          moreCalls.push(axios.get(`${API_URL}/api/placements?page=${pg}&limit=500`, { withCredentials: true }));
        }
        const moreResults = await Promise.all(moreCalls);
        for (const r of moreResults) {
          allPlacements.push(...(r.data?.placements || []));
        }
      }
      setPlacements(allPlacements);
      setUsers(uRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const orgs = [...new Set(placements.map(p => p.organization || p.company || '').filter(Boolean))];
  const teams = [...new Set(users.map(u => u.team || '').filter(Boolean))];
  const recruiters = [...new Set(placements.map(p => p.created_by_name || '').filter(Boolean))];

  const getExportDateRange = () => {
    if (fDateFrom && fDateTo) return { start: fDateFrom, end: fDateTo };
    if (fMonth !== 'all' && fYear !== 'all') {
      const y = parseInt(fYear, 10);
      const m = parseInt(fMonth, 10);
      const lastDay = new Date(y, m, 0).getDate();
      const mm = String(m).padStart(2, '0');
      return { start: `${y}-${mm}-01`, end: `${y}-${mm}-${String(lastDay).padStart(2, '0')}` };
    }
    const d = new Date();
    return { start: `${d.getFullYear()}-01-01`, end: d.toISOString().slice(0, 10) };
  };

  const handleDetailedExport = async () => {
    setExporting(true);
    try {
      const { start, end } = getExportDateRange();
      const params = new URLSearchParams({ start_date: start, end_date: end });
      if (fTeam !== 'all') params.set('team', fTeam);
      const res = await axios.get(`${API_URL}/api/admin/payroll-export?${params}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Payroll_Report_${start}_${end}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Detailed export failed:', e);
      alert('Export failed. Please try again or use the preview export.');
    } finally {
      setExporting(false);
    }
  };

  const filtered = placements.filter(p => {
    if (!matchesPayrollStatus(p.status, fStatus)) return false;
    if (fOrg !== 'all' && (p.organization || p.company) !== fOrg) return false;
    if (fRecruiter !== 'all' && p.created_by_name !== fRecruiter) return false;
    if (fMonth !== 'all' && String(p.placement_month) !== fMonth) return false;
    if (fYear !== 'all' && String(p.placement_year) !== fYear) return false;
    if (fDateFrom && (p.created_at || '') < fDateFrom) return false;
    if (fDateTo && (p.created_at || '') > fDateTo + 'T23:59:59') return false;
    if (fSearch) {
      const s = fSearch.toLowerCase();
      if (!(p.candidate_name || '').toLowerCase().includes(s) &&
          !(p.created_by_name || '').toLowerCase().includes(s) &&
          !(p.vendor || '').toLowerCase().includes(s) &&
          !(p.client || '').toLowerCase().includes(s)) return false;
    }
    if (fTeam !== 'all') {
      const recruiterUser = users.find(u => u.id === p.created_by);
      if (!recruiterUser || recruiterUser.team !== fTeam) return false;
    }
    return true;
  });

  // Apply preview toggles
  const previewData = filtered.filter(p => {
    if (showApproved && !showRejected) return isApprovedStatus(p.status);
    if (!showApproved && showRejected) return isRejectedStatus(p.status);
    if (showApproved && showRejected) return true;
    return isApprovedStatus(p.status);
  });

  const placementIncentive = (p) => {
    const base = p.overrides?.total_placement || p.incentives?.total_placement || 0;
    const bonuses = (p.additional_bonuses || []).reduce((s, b) => s + (b.amount || 0), 0);
    return base + bonuses;
  };
  const totalIncentive = previewData.reduce((s, p) => s + placementIncentive(p), 0);
  const totalMargin = previewData.reduce((s, p) => s + (p.margin || 0), 0);

  // Compute member incentive summary
  const memberSummary = {};
  if (showMemberIncentives) {
    previewData.forEach(p => {
      const name = p.created_by_name || 'Unknown';
      if (!memberSummary[name]) memberSummary[name] = { name, placements: 0, approved: 0, total_incentive: 0 };
      memberSummary[name].placements += 1;
      if (isApprovedStatus(p.status)) {
        memberSummary[name].approved += 1;
        memberSummary[name].total_incentive += placementIncentive(p);
      }
    });
  }

  const activeFilterCount = [fOrg !== 'all', fStatus !== 'approved', fRecruiter !== 'all', fTeam !== 'all', fMonth !== String(new Date().getMonth() + 1), fYear !== String(new Date().getFullYear()), fDateFrom, fDateTo, fSearch].filter(Boolean).length;
  const resetFilters = () => { setFOrg('all'); setFStatus('approved'); setFRecruiter('all'); setFTeam('all'); setFMonth(String(new Date().getMonth() + 1)); setFYear(String(new Date().getFullYear())); setFDateFrom(''); setFDateTo(''); setFSearch(''); };

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="payroll-export-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Payroll Report</h1>
          <p className="text-slate-500 mt-1">Filter, preview, and export payroll data to Excel</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => setShowExport(true)} data-testid="payroll-preview-btn">
            <Eye size={16} /> Preview
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" onClick={handleDetailedExport} disabled={exporting} data-testid="payroll-export-btn">
            {exporting ? <SpinnerGap size={16} className="animate-spin" /> : <DownloadSimple size={16} />}
            Export Detailed Report
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-slate-500 font-semibold">Filtered Records</p>
          <p className="text-2xl font-bold text-blue-700" data-testid="payroll-count">{previewData.length}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-slate-500 font-semibold">Total Incentives</p>
          <p className="text-lg font-bold text-emerald-700">{fmtINR(totalIncentive)}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-slate-500 font-semibold">Total Margin</p>
          <p className="text-lg font-bold text-teal-700">{fmtUSD(totalMargin)}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-[10px] uppercase text-slate-500 font-semibold">Avg Margin / Placement</p>
          <p className="text-lg font-bold text-slate-700">{fmtUSD(previewData.length > 0 ? totalMargin / previewData.length : 0)}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <FilterPanel onApply={() => {}} onReset={resetFilters} activeCount={activeFilterCount}>
        <FilterField label="Search">
          <Input placeholder="Candidate, recruiter, vendor, client..." value={fSearch} onChange={e => setFSearch(e.target.value)} className="h-9" data-testid="payroll-filter-search" />
        </FilterField>
        <FilterField label="Status">
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="h-9" data-testid="payroll-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="approved">Approved / Payment Confirmed</SelectItem>
              <SelectItem value="sa_approved">Payment Confirmed Only</SelectItem>
              <SelectItem value="pending_lead">Pending Lead Review</SelectItem>
              <SelectItem value="pending_manager">Pending Manager Review</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="rejected">Rejected / Backouts</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Organization">
          <Select value={fOrg} onValueChange={setFOrg}>
            <SelectTrigger className="h-9" data-testid="payroll-filter-org"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
              {orgs.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Recruiter">
          <Select value={fRecruiter} onValueChange={setFRecruiter}>
            <SelectTrigger className="h-9" data-testid="payroll-filter-recruiter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Recruiters</SelectItem>
              {recruiters.sort().map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Team">
          <Select value={fTeam} onValueChange={setFTeam}>
            <SelectTrigger className="h-9" data-testid="payroll-filter-team"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.sort().map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Month">
          <Select value={fMonth} onValueChange={setFMonth}>
            <SelectTrigger className="h-9" data-testid="payroll-filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Year">
          <Select value={fYear} onValueChange={setFYear}>
            <SelectTrigger className="h-9" data-testid="payroll-filter-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Date From">
          <Input type="date" value={fDateFrom} onChange={e => setFDateFrom(e.target.value)} className="h-9" data-testid="payroll-filter-from" />
        </FilterField>
        <FilterField label="Date To">
          <Input type="date" value={fDateTo} onChange={e => setFDateTo(e.target.value)} className="h-9" data-testid="payroll-filter-to" />
        </FilterField>
      </FilterPanel>

      {/* Preview Category Toggles */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-6">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Preview Sections:</p>
            <SwitchToggle checked={showApproved} onChange={setShowApproved} label="All Approved" testId="toggle-approved" />
            <SwitchToggle checked={showRejected} onChange={setShowRejected} label="All Rejected/Backouts" testId="toggle-rejected" />
            <SwitchToggle checked={showSummary} onChange={setShowSummary} label="Summary Cards" testId="toggle-summary" />
            <SwitchToggle checked={showMemberIncentives} onChange={setShowMemberIncentives} label="Member Incentives" testId="toggle-member-incentives" />
          </div>
        </CardContent>
      </Card>

      {/* Summary Section (togglable) */}
      {showSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-emerald-200 bg-emerald-50/50"><CardContent className="p-3 text-center">
            <p className="text-[10px] uppercase text-emerald-600 font-semibold">Approved</p>
            <p className="text-xl font-bold text-emerald-700">{previewData.filter(p => isApprovedStatus(p.status)).length}</p>
          </CardContent></Card>
          <Card className="border-red-200 bg-red-50/50"><CardContent className="p-3 text-center">
            <p className="text-[10px] uppercase text-red-600 font-semibold">Rejected/Backouts</p>
            <p className="text-xl font-bold text-red-700">{previewData.filter(p => isRejectedStatus(p.status)).length}</p>
          </CardContent></Card>
          <Card className="border-blue-200 bg-blue-50/50"><CardContent className="p-3 text-center">
            <p className="text-[10px] uppercase text-blue-600 font-semibold">Total Incentive</p>
            <p className="text-lg font-bold text-blue-700">{fmtINR(totalIncentive)}</p>
          </CardContent></Card>
          <Card className="border-amber-200 bg-amber-50/50"><CardContent className="p-3 text-center">
            <p className="text-[10px] uppercase text-amber-600 font-semibold">Unique Recruiters</p>
            <p className="text-xl font-bold text-amber-700">{new Set(previewData.map(p => p.created_by_name)).size}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Member Incentives Section (togglable) */}
      {showMemberIncentives && Object.keys(memberSummary).length > 0 && (
        <Card className="border-slate-200" data-testid="member-incentives-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-['Outfit']">Member Incentive Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {['Recruiter', 'Total Placements', 'Approved', 'Total Incentive (INR)'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left font-semibold text-slate-500 text-[10px] uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {Object.values(memberSummary).sort((a, b) => b.total_incentive - a.total_incentive).map((m, i) => (
                    <tr key={i} className="hover:bg-slate-50/70">
                      <td className="px-4 py-2 font-medium text-slate-800">{m.name}</td>
                      <td className="px-4 py-2 text-slate-600">{m.placements}</td>
                      <td className="px-4 py-2 font-medium text-emerald-700">{m.approved}</td>
                      <td className="px-4 py-2 font-bold text-emerald-700">{fmtINR(m.total_incentive)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
              <Eye size={18} className="text-blue-600" /> Data Preview
              <Badge variant="secondary" className="text-[10px]">{previewData.length} records</Badge>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  {['#', 'Candidate', 'Module', 'Recruiter', 'Team', 'Category', 'MSP/Vendor', 'Client', 'Bill Rate', 'Pay Rate', 'Margin', 'Incentive', 'Override', 'Status', 'Start Date'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left font-semibold text-slate-500 text-[10px] uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {previewData.slice(0, 100).map((p, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/70" data-testid={`payroll-row-${idx}`}>
                    <td className="px-3 py-2 text-slate-400 font-mono">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{p.candidate_name}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-[9px]">{p.module === 'sales' ? 'Bench Sales' : 'Recruiting'}</Badge></td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{p.created_by_name}</td>
                    <td className="px-3 py-2 text-slate-600">{(() => { const u = users.find(x => x.id === p.created_by); return u?.team || '-'; })()}</td>
                    <td className="px-3 py-2 text-slate-600">{p.category?.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{p.vendor}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{p.client}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtUSD(p.bill_rate)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtUSD(p.pay_rate)}</td>
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{fmtUSD(p.margin)}</td>
                    <td className="px-3 py-2 font-bold text-emerald-700 whitespace-nowrap">{fmtINR(p.overrides?.total_placement || p.incentives?.total_placement)}</td>
                    <td className="px-3 py-2">{p.overrides?.reason ? <Badge className="bg-amber-100 text-amber-700 text-[9px]">Yes</Badge> : '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${isApprovedStatus(p.status) ? 'bg-emerald-100 text-emerald-700' : isRejectedStatus(p.status) ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{p.status?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{p.start_date || p.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {previewData.length > 100 && <div className="p-3 text-center text-xs text-slate-400 bg-slate-50 border-t">Showing 100 of {previewData.length}. All will be included in export.</div>}
            {previewData.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No records match your filters</div>}
          </div>
        </CardContent>
      </Card>

      <ExportPreviewModal open={showExport} onClose={() => setShowExport(false)}
        data={previewData} fileName={`Payroll_Report_${fYear}_${fMonth}`}
        title="Payroll Report Export Preview"
        columns={[
          { key: 'candidate', header: 'Candidate Name', accessor: r => r.candidate_name },
          { key: 'module', header: 'Module', accessor: r => r.module === 'sales' ? 'Bench Sales' : 'Recruiting' },
          { key: 'recruiter', header: 'Recruiter', accessor: r => r.created_by_name },
          { key: 'team', header: 'Team', accessor: r => { const u = users.find(x => x.id === r.created_by); return u?.team || ''; } },
          { key: 'org', header: 'Organization', accessor: r => r.organization || r.company || '' },
          { key: 'category', header: 'Category', accessor: r => r.category?.replace(/_/g, ' ') },
          { key: 'vendor', header: 'MSP / Vendor', accessor: r => r.vendor },
          { key: 'client', header: 'Client', accessor: r => r.client },
          { key: 'bill_usd', header: 'Bill Rate ($)', accessor: r => r.bill_rate },
          { key: 'pay_usd', header: 'Pay Rate ($)', accessor: r => r.pay_rate },
          { key: 'margin_usd', header: 'Margin ($)', accessor: r => (r.margin || 0).toFixed(2) },
          { key: 'incentive_inr', header: 'Total Incentive (INR)', accessor: r => r.overrides?.total_placement || r.incentives?.total_placement || 0 },
          { key: 'direct_inr', header: 'Direct Payout (INR)', accessor: r => r.overrides?.total_direct_amount || r.incentives?.total_direct || 0 },
          { key: 'sr_inr', header: 'Sales Recruiter (INR)', accessor: r => r.overrides?.sales_recruiter_amount || r.incentives?.sales_recruiter || 0 },
          { key: 'src_inr', header: 'Sourcing Recruiter (INR)', accessor: r => r.overrides?.sourcing_recruiter_amount || r.incentives?.sourcing_recruiter || 0 },
          { key: 'sl_inr', header: 'Sales Lead (INR)', accessor: r => r.overrides?.sales_lead_amount || r.incentives?.sales_lead || 0 },
          { key: 'sm_inr', header: 'Sales Manager (INR)', accessor: r => r.overrides?.sales_manager_amount || r.incentives?.sales_manager || 0 },
          { key: 'srcl_inr', header: 'Sourcing Lead (INR)', accessor: r => r.overrides?.sourcing_lead_amount || r.incentives?.sourcing_lead || 0 },
          { key: 'srcm_inr', header: 'Sourcing Manager (INR)', accessor: r => r.overrides?.sourcing_manager_amount || r.incentives?.sourcing_manager || 0 },
          { key: 'split_partner', header: 'Split Partner', accessor: r => r.split_share?.user_name || '' },
          { key: 'split_pct', header: 'Split %', accessor: r => r.split_share?.percentage || '' },
          { key: 'sourcing_support', header: 'Sourcing Support', accessor: r => r.split_assignees?.sourcing_recruiter || '' },
          { key: 'has_override', header: 'Override?', accessor: r => r.overrides?.reason ? 'Yes' : 'No' },
          { key: 'override_reason', header: 'Override Reason', accessor: r => r.overrides?.reason || '' },
          { key: 'status', header: 'Status', accessor: r => r.status?.replace(/_/g, ' ') },
          { key: 'date', header: 'Created Date', accessor: r => r.created_at?.slice(0, 10) },
          { key: 'start', header: 'Start Date', accessor: r => r.start_date || '' },
          { key: '60day', header: '60 Days', accessor: r => r.sixty_day_completed || '' },
        ]}
      />
    </div>
  );
};

export default PayrollExportPage;
